import {
  AuthComponent,
  AuthFlowStep,
  EmailVerifyState,
  ForgotPasswordState,
  Session,
} from "@/types/auth"
import { LastUsedProvider, LoginAuthProvider } from "@/types/user"
import { createSelector, createSlice, PayloadAction } from "@reduxjs/toolkit"

import {
  selectOrganization,
  selectOrgNewsletterDefaultChecked,
} from "./app-slice"
import { initAppleSignIn, initGoogleSignIn } from "./auth-thunks"
import { RootState } from "./redux-store"

export type SessionStatus = "loading" | "authenticated" | "unauthenticated"

export type AuthFlowState = {
  component: AuthComponent
  step: AuthFlowStep
  callbackUrl: string
  loading: boolean
  providers: LoginAuthProvider[]
  email: string
  password: string
  cfToken: string
  customTitle?: string
  customLeadText?: string
  error?: string
  passwordTouched: boolean
  passwordErrors: string[]
  resetPasswordToken?: string
  emailLocked?: boolean
  setupTravelAssistantAfterSignup: boolean
  forgottenPassword: ForgotPasswordState
  emailVerify: EmailVerifyState
}

export type AuthDialogState = {
  open: boolean
  preventClose: boolean
  cancelCallbackUrl?: string
}

export interface AuthSliceState {
  isGoogleSignInInit: boolean
  isAppleSignInInit: boolean
  orgNewsletterOptIn: boolean | null
  session: Session | null
  sessionStatus: SessionStatus
  sessionError: string | null
  authFlow: AuthFlowState
  dialog: AuthDialogState
  lastUsedProvider: LastUsedProvider | null
}

export const initialAuthFlow = () => {
  return {
    component: "auth-form" as const,
    step: "email" as const,
    providers: [],
    email: "",
    password: "",
    cfToken: "",
    loading: false,
    callbackUrl: "/",
    passwordTouched: false,
    passwordErrors: [],
    emailLocked: false,
    setupTravelAssistantAfterSignup: true,
    forgottenPassword: {
      step: "form" as const,
      submitting: false,
    },
    emailVerify: {
      step: "form" as const,
      verifying: false,
      resending: false,
      message: undefined,
      error: undefined,
    },
  } satisfies AuthFlowState
}

const initialDialogState = () => {
  return {
    open: false,
    preventClose: false,
  } satisfies AuthDialogState
}

export const authSliceInitialState: AuthSliceState = {
  isGoogleSignInInit: false,
  isAppleSignInInit: false,
  orgNewsletterOptIn: null,
  session: null,
  sessionStatus: "loading",
  sessionError: null,
  authFlow: initialAuthFlow(),
  dialog: initialDialogState(),
  lastUsedProvider: null,
}

const authSlice = createSlice({
  name: "authSlice",
  initialState: authSliceInitialState,
  reducers: {
    setOrgNewsletterOptIn: (state, action: PayloadAction<boolean>) => {
      state.orgNewsletterOptIn = action.payload
    },
    setLastUsedProvider: (
      state,
      action: PayloadAction<LastUsedProvider | null>
    ) => {
      state.lastUsedProvider = action.payload
    },
    setSession: (state, action: PayloadAction<Session>) => {
      state.session = action.payload
      state.sessionStatus = action.payload ? "authenticated" : "unauthenticated"
      state.sessionError = null
    },
    setAuthFlow: (state, action: PayloadAction<AuthFlowState>) => {
      state.authFlow = action.payload
    },
    setAuthFlowEmail: (state, action: PayloadAction<string>) => {
      state.authFlow.email = action.payload
    },
    setAuthFlowPassword: (state, action: PayloadAction<string>) => {
      state.authFlow.password = action.payload
    },
    setAuthFlowCfToken: (state, action: PayloadAction<string>) => {
      state.authFlow.cfToken = action.payload
    },
    setAuthFlowError: (state, action: PayloadAction<string | undefined>) => {
      state.authFlow.error = action.payload
    },
    setAuthFlowLoading: (state, action: PayloadAction<boolean>) => {
      state.authFlow.loading = action.payload
    },
    setAuthFlowPasswordTouched: (state, action: PayloadAction<boolean>) => {
      state.authFlow.passwordTouched = action.payload
    },
    setAuthFlowPasswordErrors: (state, action: PayloadAction<string[]>) => {
      state.authFlow.passwordErrors = action.payload
    },
    setAuthFlowForgottenPassword: (
      state,
      action: PayloadAction<ForgotPasswordState>
    ) => {
      state.authFlow.forgottenPassword = action.payload
    },
    setAuthFlowEmailVerify: (
      state,
      action: PayloadAction<EmailVerifyState>
    ) => {
      state.authFlow.emailVerify = action.payload
    },
    _setAuthDialogProps: (state, action: PayloadAction<AuthDialogState>) => {
      state.dialog = action.payload
    },
    _resetAuthDialogProps: (state) => {
      state.dialog = initialDialogState()
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(initGoogleSignIn.fulfilled, (state) => {
        state.isGoogleSignInInit = true
      })
      .addCase(initGoogleSignIn.rejected, (state) => {
        state.isGoogleSignInInit = false
      })
      .addCase(initAppleSignIn.fulfilled, (state) => {
        state.isAppleSignInInit = true
      })
  },
})

// Base selector
const selectSliceState = (state: RootState) => state.authSlice

// Simple selectors
export const selectIsGoogleSignInInit = createSelector(
  [selectSliceState],
  (state) => state.isGoogleSignInInit
)

export const selectIsAppleSignInInit = createSelector(
  [selectSliceState],
  (state) => state.isAppleSignInInit
)

export const selectOrgNewsletterOptIn = createSelector(
  [selectSliceState, selectOrgNewsletterDefaultChecked],
  (state, defaultChecked) => state.orgNewsletterOptIn ?? defaultChecked
)

export const selectLastUsedProvider = createSelector(
  [selectSliceState],
  (state) => state.lastUsedProvider
)

export const selectSession = createSelector(
  [selectSliceState],
  (state) => state.session
)

export const selectSessionStatus = createSelector(
  [selectSliceState],
  (state) => state.sessionStatus
)

const selectAuthDialogProps = createSelector(
  [selectSliceState],
  (state) => state.dialog
)

export const selectAuthDialogOpen = createSelector(
  [selectAuthDialogProps],
  (dialog) => dialog.open
)

export const selectAuthPreventClose = createSelector(
  [selectAuthDialogProps],
  (dialog) => dialog.preventClose
)

export const selectAuthFlow = createSelector(
  [selectSliceState],
  (state) => state.authFlow
)

export const selectAuthFlowComponent = createSelector(
  [selectAuthFlow],
  (authFlow) => authFlow.component
)

export const selectAuthFlowStep = createSelector(
  [selectAuthFlow],
  (authFlow) => authFlow.step
)

export const selectAuthFlowEmail = createSelector(
  [selectAuthFlow],
  (authFlow) => {
    return authFlow.email
  }
)

export const selectAuthFlowPassword = createSelector(
  [selectAuthFlow],
  (authFlow) => authFlow.password
)

export const selectAuthFlowCfToken = createSelector(
  [selectAuthFlow],
  (authFlow) => authFlow.cfToken
)

export const selectAuthFlowCallbackUrl = createSelector(
  [selectAuthFlow],
  (authFlow) => authFlow.callbackUrl
)

export const selectAuthFlowProviders = createSelector(
  [selectAuthFlow],
  (authFlow) => authFlow.providers
)

export const selectAuthFlowLoading = createSelector(
  [selectAuthFlow],
  (authFlow) => authFlow.loading
)

export const selectAuthFlowError = createSelector(
  [selectAuthFlow],
  (authFlow) => authFlow.error
)

export const selectAuthFlowPasswordTouched = createSelector(
  [selectAuthFlow],
  (authFlow) => authFlow.passwordTouched
)

export const selectAuthFlowCustomTitle = createSelector(
  [selectAuthFlow],
  (authFlow) => authFlow.customTitle
)

export const selectAuthFlowCustomLeadText = createSelector(
  [selectAuthFlow],
  (authFlow) => authFlow.customLeadText
)

export const selectAuthFlowPasswordErrors = createSelector(
  [selectAuthFlow],
  (authFlow) => authFlow.passwordErrors
)

export const selectAuthFlowResetPasswordToken = createSelector(
  [selectAuthFlow],
  (authFlow) => authFlow.resetPasswordToken
)

export const selectAuthFlowEmailLocked = createSelector(
  [selectAuthFlow],
  (authFlow) => authFlow.emailLocked ?? false
)

export const selectAuthFlowForgottenPassword = createSelector(
  [selectAuthFlow],
  (authFlow) => authFlow.forgottenPassword
)

export const selectAuthFlowForgotPasswordStep = createSelector(
  [selectAuthFlowForgottenPassword],
  (forgottenPassword) => forgottenPassword.step
)

export const selectAuthFlowForgotPasswordSubmitting = createSelector(
  [selectAuthFlowForgottenPassword],
  (forgottenPassword) => forgottenPassword.submitting
)

export const selectAuthFlowEmailVerify = createSelector(
  [selectAuthFlow],
  (authFlow) => authFlow.emailVerify
)

export const selectAuthFlowEmailVerifyStep = createSelector(
  [selectAuthFlowEmailVerify],
  (emailVerify) => emailVerify.step
)

export const selectAuthFlowEmailVerifyVerifying = createSelector(
  [selectAuthFlowEmailVerify],
  (emailVerify) => emailVerify.verifying
)

export const selectAuthFlowEmailVerifyResending = createSelector(
  [selectAuthFlowEmailVerify],
  (emailVerify) => emailVerify.resending
)

export const selectAuthFlowEmailVerifyMessage = createSelector(
  [selectAuthFlowEmailVerify],
  (emailVerify) => emailVerify.message
)

export const selectAuthFlowEmailVerifyError = createSelector(
  [selectAuthFlowEmailVerify],
  (emailVerify) => emailVerify.error
)

export const selectAuthFlowEmailVerifyDisplayEmail = createSelector(
  [selectAuthFlowEmailVerify],
  (emailVerify) => emailVerify.displayEmail
)

// Computed selectors for auth form header logic

export const selectAuthFlowHeaderTitle = createSelector(
  [
    selectAuthFlowComponent,
    selectAuthFlowStep,
    selectAuthFlowCustomTitle,
    selectOrganization,
    selectAuthFlowForgottenPassword,
    selectAuthFlowEmailVerify,
  ],
  (
    component,
    step,
    customTitle,
    organization,
    forgottenPassword,
    emailVerify
  ) => {
    switch (component) {
      case "auth-form":
        return (() => {
          switch (step) {
            case "signin":
              return "Log in"
            case "register":
              if (customTitle) return customTitle
              return "Create an account"
            case "email":
              if (customTitle) return customTitle
              return organization ? "Welcome" : "Log in or sign up"
          }
        })()
      case "verify_email":
        return (() => {
          switch (emailVerify.step) {
            case "form":
              return "One quick thing…"
            case "verified":
              return "You’re done!"
          }
        })()
      case "forgotten":
        return (() => {
          switch (forgottenPassword.step) {
            case "form":
            case "mismatch":
              return "Reset password"
            case "success":
              return "Email sent"
          }
        })()
      case "reset_password":
        return "Reset password"
    }
  }
)

export const selectAuthFlowLeadText = createSelector(
  [
    selectAuthFlowComponent,
    selectAuthFlowCustomLeadText,
    selectAuthFlowForgottenPassword,
  ],
  (component, customLeadText, forgottenPassword) => {
    // Handle component-specific lead text first, before checking customLeadText
    if (component === "forgotten") {
      switch (forgottenPassword.step) {
        case "form":
          return undefined
        case "mismatch":
          return "Looks like your email is linked to a social login. No need for a password here! Simply log in via your social media account for instant access."
        case "success":
          // Success message has dynamic content with email, handled in component
          return undefined
      }
    }

    if (component === "verify_email") {
      // Email verify has dynamic content with user email, handled in component
      return undefined
    }

    if (component === "reset_password") {
      return undefined
    }

    // The auth-form steps only show a caller's customLeadText
    return customLeadText || undefined
  }
)

export const {
  setOrgNewsletterOptIn,
  setLastUsedProvider,
  setSession,
  setAuthFlow,
  setAuthFlowEmail,
  setAuthFlowPassword,
  setAuthFlowCfToken,
  setAuthFlowError,
  setAuthFlowLoading,
  setAuthFlowPasswordTouched,
  setAuthFlowPasswordErrors,
  setAuthFlowForgottenPassword,
  setAuthFlowEmailVerify,
  _setAuthDialogProps,
  _resetAuthDialogProps,
} = authSlice.actions

export default authSlice
